import { Parser } from "binary-parser";
import pako from "pako";

import { IDataPointState, IDataSystem } from "@/interfaces";
import { IVessel } from "@/shared/user";

import { IModule } from "./user";

export interface ILatLng {
  lat: number;
  lng: number;
}

export interface IPlaybackShip {
  position: ILatLng;
  heading: number;
}

export enum LatLng {
  Latitude,
  Longitude,
}

export interface ICoordinate {
  x: number;
  y: number;
}

export enum ControlType {
  Header = "Header",
  ToggleHeader = "ToggleHeader",
  Link = "Link",
  Toggle = "Toggle",
}

export interface ISidebarItem {
  id: string;
  name: string;
  controlType: ControlType;
  isToggled: boolean;
  items: ISidebarItem[];
}

export class GlobalHelper {
  public static getModuleNameById(id: number) {
    switch (id) {
      case 0:
        return "module.landing";
      case 1:
        return "module.alarms_events";
      case 2:
        return "module.cctv";
      case 3:
        return "module.dashboards";
      case 4:
        return "module.datahub";
      case 5:
        return "module.dp";
      case 6:
        return "module.fuel";
      case 7:
        return "module.locator";
      case 8:
        return "module.helm";
      case 9:
        return "module.network";
      case 10:
        return "module.playback";
      case 11:
        return "module.reporting";
      case 12:
        return "module.trends";
      case 13:
        return "module.event_log";
      case 14:
        return "module.fleet_management";
      case 15:
        return "module.nesti4";
      case 16:
        return "module.vessel_info";
      case 17:
        return "module.cobras";
      case 19:
        return "module.backoffice";
      case 20:
        return "module.access_control";
      case 21:
        return "module.easog";
      default:
        return "";
    }
  }

  public static getIconById(id: number) {
    switch (id) {
      case 1: //Alarms & Events
        return "bell";
      case 2: //CCTV
        return "cctv";
      case 3: //Dashboards
        return "monitor-dashboard";
      case 4: //Data Hub
        return "bridge";
      case 5: //DP
        return "ship-wheel";
      case 6: //Fuel
        return "barrel";
      case 7: //Locator
        return "map-marker";
      case 8: //Helm
        return "lifebuoy";
      case 9: //Network
        return "transit-connection-variant";
      case 10: //Playback
        return "replay";
      case 11: //Reporting
        return "tablet-dashboard";
      case 12: //Trends
        return "chart-line";
      case 13: //Event Log
        return "math-log";
      case 14: //Fleet Management
        return "dots-triangle";
      case 15: //NESTI4
        return "sine-wave";
      case 16: //Vessel Info
        return "information-outline";
      case 17: //COBRAS
        return "chart-sankey-variant";
      case 19: //Backoffice
        return "chair-rolling";
      case 20: //Acess Control
        return "account-key";
      case 21: //EAsog
        return "format-list-bulleted-square";
      case 100: //Settings
        return "account-cog";
      default:
        return "";
    }
  }

  public static getRouteById(id: number) {
    switch (id) {
      case 0: //Landing
        return "landing";
      case 1: //Alarms & Events
        return "alarms";
      case 2: //CCTV
        return "cctv";
      case 3: //Dashboards
        return "dashboards";
      case 4: //Data Hub
        return "dhub";
      case 5: //DP
        return "dp";
      case 6: //Fuel
        return "fuel";
      case 7: //Locator
        return "locator";
      case 8: //Helm
        return "helm";
      case 9: //Network
        return "ndm";
      case 10: //Playback
        return "playback";
      case 11: //Reporting
        return "pb-reporting";
      case 12: //Trends
        return "trends";
      case 13: //Event Log
        return "events";
      case 14: //Fleet Management
        return "fleet-management";
      case 15: //NESTI4
        return "http://10.142.1.101:4513/en/home";
      case 16: //Vessel Info
        return "vessel";
      case 17: //COBRAS
        return "cobras";
      case 19: //Backoffice
        return "backoffice";
      case 20: //Access Control
        return "access-control";
      case 21: //EAsog
        return "easog";
      default:
        return "";
    }
  }

  public static moduleExists(id: number, modules: IModule[]): boolean {
    for (const module of modules) {
      if (module.id === id) {
        return true;
      }
    }
    return false;
  }

  public static addModuleNames(modules: IModule[]) {
    modules.forEach((module) => {
      module.name = GlobalHelper.getModuleNameById(module.id);
    });
  }

  public static filterModules(modules: IModule[], exclusionList: number[]): IModule[] {
    return modules.filter((module) => !exclusionList.includes(module.id));
  }

  public static sortModules(modules: IModule[]): IModule[] {
    return [...modules].sort((entryA, entryB) =>
      entryA.name.localeCompare(entryB.name, "en", { sensitivity: "base" })
    );
  }

  public static sortAndFilterModules(modules: IModule[], exclusionList?: number[]): IModule[] {
    let sortedAndFilteredModules: IModule[];

    if (exclusionList) {
      sortedAndFilteredModules = this.filterModules(modules, exclusionList);
    }

    return this.sortModules(sortedAndFilteredModules);
  }

  public static sortVessels(vessels: IVessel[]) {
    return [...vessels].sort((entryA, entryB) => entryA.name.localeCompare(entryB.name));
  }
}

export function changeColorBrightness(hexColor: string, amount: number): string {
  //remove "#" if present
  hexColor = hexColor.replace("#", "");

  //check for valid hex format
  if (hexColor.length !== 6) {
    throw new Error("Invalid hex color format");
  }

  //convert hex to RGB components
  const r = parseInt(hexColor.slice(0, 2), 16);
  const g = parseInt(hexColor.slice(2, 4), 16);
  const b = parseInt(hexColor.slice(4, 6), 16);

  //adjust brightness
  const adjust = Math.round(255 * amount); // Adjust intensity
  const newR = Math.min(255, Math.max(0, r + adjust));
  const newG = Math.min(255, Math.max(0, g + adjust));
  const newB = Math.min(255, Math.max(0, b + adjust));

  //convert back to hex (with padding)
  const newHexColor =
    "#" +
    newR.toString(16).padStart(2, "0") +
    newG.toString(16).padStart(2, "0") +
    newB.toString(16).padStart(2, "0");

  return newHexColor;
}

export function getJson(gzipFile: ArrayBuffer): string {
  try {
    //decopmress the gzip file
    const decompressedData = pako.inflate(gzipFile, { to: "string" });

    // decode into a text string
    // const decoder = new TextDecoder("utf-8");
    // const decodedText = decoder.decode(decompressedData);

    return decompressedData;
  } catch (error) {
    return "";
  }
}

// ! The following functions MUST remain "function" keyword functions
// ! since they work properly only if "this" keyword is bound to
// ! the calling context
// TODO delete after full conversion
export function fetchAndPopulateVesselDropdown() {
  this.api.getVesselList().forEach((vessel: IVessel) =>
    this.listOfVessels.push({
      name: vessel.name,
      value: vessel.id.toString(),
    })
  );
}

export function fetchAndPopulateSystemDropdown() {
  this.api.getDataSystemList().then(({ data }: { data: IDataSystem[] }) =>
    data.forEach((system) =>
      this.listOfDataSystems.push({
        name: system.dataSystemName,
        value: system.dataSystemId.toString(),
      })
    )
  );
}

export function sortMapByCategoryAndDatapoint(data: Map<string, IDataPointState[]>) {
  // Helper function to split a string into prefix and number
  const getParts = (str: string | undefined) => {
    const safeStr = (str ?? "").trim(); // Trim whitespace
    const match = safeStr.match(/^(.*?)(\d+)$/);

    if (match) {
      return {
        prefix: match[1].toLowerCase().trim(), // prefix, e.g., "thruster", "vru", etc
        number: parseInt(match[2], 10), // number part e.g., 1, 3, 5, etc
      };
    }

    return { prefix: safeStr.toLowerCase().trim(), number: 0 };
  };

  // Sort the Map keys
  const sortedData = Array.from(data.entries()).sort((a, b) => {
    const partsA = getParts(a[0]);
    const partsB = getParts(b[0]);
    const prefixCompare = partsA.prefix.localeCompare(partsB.prefix);

    return prefixCompare !== 0 ? prefixCompare : partsA.number - partsB.number;
  });

  // Sort the DataPointState arrays
  sortedData.forEach(([category, datapoints]) => {
    datapoints.sort((a, b) => {
      const labelA = a.dpLabel || a.label || "";
      const labelB = b.dpLabel || b.label || "";
      const partsA = getParts(labelA);
      const partsB = getParts(labelB);
      const prefixCompare = partsA.prefix.localeCompare(partsB.prefix);

      return prefixCompare !== 0 ? prefixCompare : partsA.number - partsB.number;
    });
  });

  // Clear and repopulate the original Map
  data.clear();
  sortedData.forEach(([key, value]) => data.set(key, value));
}

const MAX_NO_GYRO = 5;
const MAX_NO_WIND = 5;
const MAX_NO_VRU = 5;
const MAX_NO_SPEED = 3;
const MAX_NO_ROT = 3;
const MAX_NO_EXT_FORCE = 2;
const MAX_NO_PLAYBACK_NAV = 15;
const MAX_NO_GPS = 4;
const MAX_NO_THRUSTER = 10;
const MAX_NO_GENERATOR = 10;
const MAX_NO_BUSES = 8;
const MAX_NO_MAIN_ENGINE = 4;
const GUI_MAX_GROUPS = 24;
const GUI_MAX_ANALOG_IND = 10;

export const DPSParser = new Parser()
  .endianness("little")
  .int16le("MainMode")
  .int16le("SubMode")
  .array("PositionDeviation", { type: "floatle", length: 2 })
  .floatle("HeadingDeviation")
  .array("VesselPos", { type: "doublele", length: 2 })
  .floatle("Heading")
  .array("Speed", { type: "floatle", length: 2 })
  .floatle("RotSpeed")
  .array("NavigatorStatus", { type: "int16le", length: MAX_NO_PLAYBACK_NAV })
  .array("NavigatorNotAccepted", { type: "uint8", length: MAX_NO_PLAYBACK_NAV })
  .array("TargetID", { type: "int16le", length: MAX_NO_PLAYBACK_NAV })
  .array("TargetType", { type: "int16le", length: MAX_NO_PLAYBACK_NAV })
  .array("NavigatorPosition", {
    type: new Parser().endianness("little").array("latLon", { type: "doublele", length: 2 }),
    length: MAX_NO_PLAYBACK_NAV,
  })
  .array("TargetPosition", {
    type: new Parser().endianness("little").array("latLon", { type: "doublele", length: 2 }),
    length: MAX_NO_PLAYBACK_NAV,
  })
  .array("GpsVruComp", {
    type: new Parser().endianness("little").array("vru", { type: "doublele", length: 2 }),
    length: MAX_NO_GPS,
  })
  .array("GpsXYDist", {
    type: new Parser().endianness("little").array("xy", { type: "doublele", length: 2 }),
    length: MAX_NO_GPS,
  })
  .array("GyroHeading", { type: "floatle", length: MAX_NO_GYRO })
  .array("GyroStatus", { type: "int16le", length: MAX_NO_GYRO })
  .array("GyroNotAccepted", { type: "uint8", length: MAX_NO_GYRO })
  .array("VRU", {
    type: new Parser().endianness("little").array("rollPitchHeave", { type: "floatle", length: 3 }),
    length: MAX_NO_VRU,
  })
  .array("VRUStatus", { type: "int16le", length: MAX_NO_VRU })
  .array("Wind", {
    type: new Parser().endianness("little").array("speedAngle", { type: "floatle", length: 2 }),
    length: MAX_NO_WIND,
  })
  .array("WindStatus", { type: "int16le", length: MAX_NO_WIND })
  .array("ExtForceSensor", {
    type: new Parser().endianness("little").array("force", { type: "floatle", length: 2 }),
    length: MAX_NO_EXT_FORCE,
  })
  .array("RotSensor", { type: "floatle", length: MAX_NO_ROT })
  .floatle("WindDirection")
  .floatle("WindSpeed")
  .floatle("CurrentDirection")
  .floatle("CurrentSpeed")
  .array("CarrotPosition", { type: "doublele", length: 2 })
  .floatle("CarrotHeading")
  .array("PositionSetpoint", { type: "doublele", length: 2 })
  .floatle("HeadingSetpoint")
  .array("ThrusterType", { type: "int16le", length: MAX_NO_THRUSTER })
  .array("ThrusterStatus", { type: "int16le", length: MAX_NO_THRUSTER })
  .array("RPMCommand", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("RPMFeedback", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("PitchCommand", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("PitchFeedback", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("RelativeThrustCommand", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("RelativeThrustFeedback", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("AzimuthCommand", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("AzimuthFeedback", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("ThrPowerFeedback", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("AxisControl", { type: "uint8", length: 3 })
  .array("ForceCmd", { type: "floatle", length: 3 })
  .array("ForceFdb", { type: "floatle", length: 3 })
  .array("DebugData", { type: "floatle", length: 10 })
  .array("TCVoteStatus", { type: "int16le", length: 6 })
  .array("DPCC_TCSCCInUse", { type: "int16le", length: 3 })
  .array("TCSCC_DPCCInUse", { type: "int16le", length: 3 })
  .array("CCVoteBuffer", {
    type: new Parser().endianness("little").array("votes", { type: "int16le", length: 5 }),
    length: 3,
  })
  .array("CarrotVelocity", { type: "floatle", length: 3 })
  .array("BallisticSpeed", { type: "floatle", length: 3 })
  .array("ControllerP", { type: "floatle", length: 3 })
  .array("ControllerD", { type: "floatle", length: 3 })
  .array("ControllerI", { type: "floatle", length: 3 })
  .array("ControllerWindFF", { type: "floatle", length: 3 })
  .array("ControllerAccFF", { type: "floatle", length: 3 })
  .array("GeneratorLoad", { type: "floatle", length: MAX_NO_GENERATOR })
  .array("DieselLoad", { type: "floatle", length: MAX_NO_MAIN_ENGINE })
  .array("GenBreakerStatus", { type: "uint8", length: MAX_NO_GENERATOR })
  .array("ThrBreakerStatus", { type: "uint8", length: MAX_NO_THRUSTER })
  .array("BusBreakerStatus", { type: "uint8", length: MAX_NO_BUSES })
  .array("NoOfGenConnected", { type: "int16le", length: MAX_NO_BUSES })
  .array("NoOfThrConnected", { type: "int16le", length: MAX_NO_BUSES })
  .array("CCPrefCC", { type: "int16le", length: 3 })
  .int16le("CCInUse")
  .int16le("TCSCCInUse")
  .int16le("OSInCmd")
  .array("EstimatorThrForce", { type: "floatle", length: 3 })
  .array("EstimatorBiasForce", { type: "floatle", length: 3 })
  .array("EstimatorDragForce", { type: "floatle", length: 3 })
  .array("EstimatorDampForce", { type: "floatle", length: 3 })
  .array("EstimatorWindForce", { type: "floatle", length: 3 })
  .array("EstimatorExtForce", { type: "floatle", length: 3 })
  .array("JoystickCmd", { type: "floatle", length: 3 })
  .int16le("OpPointNumber")
  .array("OPPointPos", { type: "floatle", length: 2 })
  .array("PIDGainSettings", { type: "floatle", length: 3 })
  .floatle("AlarmLimit")
  .int16le("FTMode")
  .int16le("FTTarget1")
  .int16le("FTTarget2")
  .int16le("FTAdmin1")
  .int16le("FTAdmin2")
  .int16le("FTTransponderID")
  .int16le("FTHPRAdminID")
  .uint8("RelHdgEnabled")
  .uint8("FTCircleEnabled")
  .floatle("FTCircleRadius")
  .array("FTCirclePos", { type: "floatle", length: 2 })
  .uint8("AllocCombinatorMode")
  .array("EngineInVariableMode", { type: "uint8", length: MAX_NO_MAIN_ENGINE })
  .array("BatteryReady", { type: "uint8", length: MAX_NO_GENERATOR })
  .array("StateOfCharge", { type: "floatle", length: MAX_NO_GENERATOR })
  .array("RemainingEnergy", { type: "floatle", length: MAX_NO_GENERATOR })
  .array("RemainingTimeMin", { type: "floatle", length: MAX_NO_GENERATOR })
  .array("RemainingTimeMinWorstCase", { type: "floatle", length: MAX_NO_GENERATOR })
  .array("GeneratorFuel", { type: "floatle", length: MAX_NO_GENERATOR })
  .array("SpeedSensor", {
    type: new Parser().endianness("little").array("speed", { type: "floatle", length: 2 }),
    length: MAX_NO_SPEED,
  })
  .array("VoithLongitudalPitchCommand", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("VoithTransversePitchCommand", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("VoithLongitudalPitchFeedback", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("VoithTransversePitchFeedback", { type: "floatle", length: MAX_NO_THRUSTER })
  .array("ExtCtrlSpdRef", { type: "floatle", length: 3 })
  .array("ExtCtrlAccRef", { type: "floatle", length: 3 })
  .uint8("AutonomyMode")
  .floatle("HeadingPMoment")
  .floatle("HeadingIMoment")
  .floatle("HeadingDMoment")
  .floatle("XTDPMoment")
  .floatle("XTDIMoment")
  .floatle("XTDDMoment")
  .floatle("YawRateFF")
  .floatle("TotalMoment")
  .floatle("CrabAngle")
  .uint8("InTurn")
  .floatle("CrabInTurn")
  .floatle("TurnRadius")
  .floatle("XTE_used")
  .floatle("XTE_raw")
  .floatle("XTE_reference")
  .floatle("HeadingReference")
  .floatle("YawRateReference")
  .floatle("WheelOverDistance")
  .uint16le("Year")
  .uint16le("Month")
  .uint16le("DayOfWeek")
  .uint16le("Day")
  .uint16le("Hour")
  .uint16le("Minute")
  .uint16le("Second")
  .uint16le("Milliseconds")
  .int16le("CRC")
  .int32le("BytePattern1")
  .int32le("BytePattern2");
